/**
 * FormField.jsx — Reusable input / textarea component
 * Matches the Sapphire Veil palette of the portfolio.
 */

/* ── Injected styles (scoped, no Tailwind needed) ── */
const FIELD_STYLES = `
  .ff-wrap {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .ff-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(185, 210, 228, 0.65);
  }

  .ff-label.ff-label--error {
    color: rgba(255, 100, 100, 0.85);
  }

  .ff-input,
  .ff-textarea {
    width: 100%;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: var(--text-strong, #f3fbff);
    font-family: 'Manrope', 'Inter', sans-serif;
    font-size: 0.95rem;
    padding: 0.85rem 1rem;
    outline: none;
    transition:
      border-color 180ms ease,
      box-shadow 180ms ease;
    resize: none;
  }

  .ff-input::placeholder,
  .ff-textarea::placeholder {
    color: rgba(185, 210, 228, 0.35);
  }

  /* Focus */
  .ff-input:focus,
  .ff-textarea:focus {
    border-color: #00e5ff;
    box-shadow:
      0 0 0 3px rgba(0, 229, 255, 0.1),
      0 8px 24px -16px rgba(0, 229, 255, 0.25);
  }

  /* Error */
  .ff-input.ff-input--error,
  .ff-textarea.ff-textarea--error {
    border-color: rgba(255, 80, 80, 0.55);
    box-shadow: 0 0 0 3px rgba(255, 80, 80, 0.08);
  }

  .ff-error-msg {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: rgba(255, 110, 110, 0.9);
    margin-top: 2px;
  }

  .ff-counter {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: rgba(185, 210, 228, 0.4);
    text-align: right;
    margin-top: 2px;
  }

  .ff-counter.ff-counter--warn {
    color: rgba(255, 180, 80, 0.75);
  }

  [data-theme="light"] .ff-label {
    color: var(--text-muted, #64748b);
  }

  [data-theme="light"] .ff-input,
  [data-theme="light"] .ff-textarea {
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(15, 23, 42, 0.12);
    color: var(--text-strong, #0f172a);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
  }

  [data-theme="light"] .ff-input::placeholder,
  [data-theme="light"] .ff-textarea::placeholder {
    color: rgba(100, 116, 139, 0.72);
  }

  [data-theme="light"] .ff-input:focus,
  [data-theme="light"] .ff-textarea:focus {
    border-color: var(--accent, #0d9488);
    box-shadow:
      0 0 0 3px rgba(13, 148, 136, 0.12),
      0 10px 24px -18px rgba(13, 148, 136, 0.26);
  }

  [data-theme="light"] .ff-counter {
    color: var(--text-muted, #64748b);
  }
`;

let fieldStylesInjected = false;

function injectFieldStyles() {
  if (fieldStylesInjected || document.getElementById('ff-styles')) return;
  const tag = document.createElement('style');
  tag.id = 'ff-styles';
  tag.textContent = FIELD_STYLES;
  document.head.appendChild(tag);
  fieldStylesInjected = true;
}

/* ── Component ── */
export default function FormField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  placeholder,
  rows,
  maxLength,
}) {
  injectFieldStyles();

  const hasError   = Boolean(error);
  const isTextarea = typeof rows === 'number';
  const showCounter = isTextarea && maxLength;

  const sharedProps = {
    id: `ff-${name}`,
    name,
    value,
    onChange,
    placeholder,
    'aria-invalid': hasError ? 'true' : undefined,
    'aria-describedby': hasError ? `ff-${name}-err` : undefined,
  };

  return (
    <div className="ff-wrap">
      <label
        htmlFor={`ff-${name}`}
        className={`ff-label${hasError ? ' ff-label--error' : ''}`}
      >
        {label}
      </label>

      {isTextarea ? (
        <textarea
          {...sharedProps}
          rows={rows}
          maxLength={maxLength}
          className={`ff-textarea${hasError ? ' ff-textarea--error' : ''}`}
        />
      ) : (
        <input
          {...sharedProps}
          type={type}
          className={`ff-input${hasError ? ' ff-input--error' : ''}`}
        />
      )}

      {showCounter && (
        <span className={`ff-counter${value.length > maxLength * 0.85 ? ' ff-counter--warn' : ''}`}>
          {value.length} / {maxLength}
        </span>
      )}

      {hasError && (
        <span
          id={`ff-${name}-err`}
          className="ff-error-msg"
          role="alert"
        >
          {error}
        </span>
      )}
    </div>
  );
}
